import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { debugLog } from "../../../common/utils/debug";
import { SendIcon } from "../../../assets/icons";
import ThinkingProcess, { ThinkingProcessStatus, CompletedIcon, InterruptedIcon } from "../ThinkingProcess";
import { usePause } from "../../../context/pause";
import { useMute } from "../../../context/mute";
import { ChatIcon } from "../../../assets/icons";
import { Button } from "@mui/material";
import { TextareaAutosize } from "@mui/material";
import { cn } from "../../../common/utils/cn";
import { formatTextWithBullets, hasBullet } from "../../../common/utils/hasBullet";
import { Message } from "../../../types";

const MutedPlaceholderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <g opacity="0.5" clipPath="url(#clip0_muted)">
      <path d="M3.75 3.125L16.25 16.875" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6.79688 2.7579C7.74557 2.19168 8.8274 1.88706 9.93215 1.87506C11.0369 1.86307 12.1251 2.14414 13.0859 2.68963C14.0466 3.23512 14.8456 4.02554 15.4014 4.98036C15.9573 5.93517 16.2501 7.02027 16.25 8.12509" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12.5 16.8749C11.8695 17.6382 11.0672 18.1249 10 18.1249C9.08833 18.1249 8.21399 17.7628 7.56933 17.1181C6.92467 16.4734 6.56251 15.5991 6.56251 14.6874C6.56251 11.446 3.75001 12.4999 3.75001 8.12491C3.74779 6.8016 4.16761 5.51205 4.94845 4.44366" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.14297 6.85699C6.96549 7.25611 6.87418 7.68817 6.87501 8.12496" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12.8125 9.37515C13.0177 8.99044 13.125 8.56115 13.125 8.12515C13.1253 7.6274 13.0066 7.13679 12.779 6.69415C12.5513 6.25152 12.2212 5.86967 11.8161 5.5804C11.4111 5.29112 10.9428 5.10279 10.4502 5.03108C9.95767 4.95937 9.4551 5.00635 8.98438 5.16812" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13.2086 13.5297C13.0209 13.6588 12.8016 13.7342 12.5742 13.7477C12.3468 13.7612 12.12 13.7124 11.9183 13.6063C11.7167 13.5003 11.5479 13.3413 11.4301 13.1463C11.3123 12.9513 11.25 12.7278 11.25 12.5C11.2488 12.1796 11.3071 11.8617 11.4219 11.5625" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_muted">
        <rect width="20" height="20" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const UnmutedPlaceholderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <g clipPath="url(#clip0_unmuted)">
      <path d="M12.5 16.875C11.8695 17.6383 11.0672 18.125 10 18.125C9.08832 18.125 8.21398 17.7628 7.56932 17.1182C6.92466 16.4735 6.5625 15.5992 6.5625 14.6875C6.5625 11.4461 3.75 12.5 3.75 8.125C3.75 6.4674 4.40848 4.87769 5.58058 3.70558C6.75269 2.53348 8.3424 1.875 10 1.875C11.6576 1.875 13.2473 2.53348 14.4194 3.70558C15.5915 4.87769 16.25 6.4674 16.25 8.125" stroke="#9CA3AF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13.5828 13.125C13.4453 13.3634 13.2329 13.5498 12.9786 13.6552C12.7244 13.7606 12.4424 13.7791 12.1765 13.7079C11.9106 13.6367 11.6757 13.4797 11.5082 13.2613C11.3406 13.0429 11.2499 12.7753 11.25 12.5C11.25 10.625 13.125 10 13.125 8.125C13.125 7.2962 12.7958 6.50134 12.2097 5.91529C11.6237 5.32924 10.8288 5 10 5C9.1712 5 8.37634 5.32924 7.79029 5.91529C7.20424 6.50134 6.875 7.2962 6.875 8.125" stroke="#9CA3AF" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_unmuted">
        <rect width="20" height="20" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const DisabledPlaceholderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <g clipPath="url(#clip0_disabled)">
      <path d="M16.25 6.875H3.75C3.40482 6.875 3.125 7.15482 3.125 7.5V16.25C3.125 16.5952 3.40482 16.875 3.75 16.875H16.25C16.5952 16.875 16.875 16.5952 16.875 16.25V7.5C16.875 7.15482 16.5952 6.875 16.25 6.875Z" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 12.8125C10.5178 12.8125 10.9375 12.3928 10.9375 11.875C10.9375 11.3572 10.5178 10.9375 10 10.9375C9.48223 10.9375 9.0625 11.3572 9.0625 11.875C9.0625 12.3928 9.48223 12.8125 10 12.8125Z" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6.875 6.875V4.375C6.875 3.5462 7.20424 2.75134 7.79029 2.16529C8.37634 1.57924 9.1712 1.25 10 1.25C10.8288 1.25 11.6237 1.57924 12.2097 2.16529C12.7958 2.75134 13.125 3.5462 13.125 4.375V6.875" stroke="#9CA3AF" strokeWidth="0.78125" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_disabled">
        <rect width="20" height="20" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const TypingText: React.FC<{ text: string }> = ({ text }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setDisplayedText("");
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text]);

  return <span>{displayedText}</span>;
};

interface StreamingTranscription {
  interimText: string;
  finalText: string;
  isStreaming: boolean;
}

interface ChatProps {
  messages: Message[];
  onSendMessage: (text: string) => void;
  avatarReady: boolean;
  isConnected?: boolean;
  sessionId?: string | null;
  streamingTranscription?: StreamingTranscription;
  thinkingStatus?: ThinkingProcessStatus;
  isSpeaking?: boolean;
  isThinking?: boolean;
}

const Chat: React.FC<ChatProps> = ({
  messages,
  onSendMessage,
  avatarReady,
  isConnected = false,
  sessionId,
  streamingTranscription = {
    interimText: "",
    finalText: "",
    isStreaming: false,
  },
  thinkingStatus = "pending",
  isSpeaking = false,
  isThinking = false,
}) => {
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isPaused } = usePause();
  const { isMuted } = useMute();

  const { interimText, finalText, isStreaming } = streamingTranscription;

  const streamingText = useMemo(() => {
    const combined = finalText + (interimText ? " " + interimText : "");
    const trimmed = combined.trim().toLowerCase();

    if (trimmed === "stop" || trimmed === "stop.") {
      return "";
    }

    return combined;
  }, [finalText, interimText]);

  useLayoutEffect(() => {
    if (streamingText && !isMuted) {
      setInputText(streamingText);
    } else if ((!isStreaming && streamingText === "") || isMuted) {
      setInputText("");
    }
  }, [streamingText, isStreaming, isMuted]);

  // Auto-scroll textarea to bottom when text changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, [inputText]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, isThinking]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (lastMessage.role === "user") {
        setIsLoading(true);
      } else if (lastMessage.role === "assistant") {
        setIsLoading(false);
      }
    }
  }, [messages]);

  const sendMessage = () => {
    debugLog("📤 [Chat] sendMessage called");
    debugLog("📤 [Chat] inputText:", inputText);
    debugLog("📤 [Chat] Timestamp:", Date.now());
    debugLog("📤 [Chat] Conditions: avatarReady=", avatarReady, "isPaused=", isPaused, "isSpeaking=", isSpeaking);
    if (inputText.trim() && avatarReady && !isPaused && !isSpeaking) {
      debugLog("📤 [Chat] Conditions met, calling onSendMessage with:", inputText.trim());
      setIsLoading(true);
      onSendMessage(inputText.trim());
      setInputText("");
    } else {
      debugLog("📤 [Chat] Conditions NOT met, message not sent");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatMessage = (message: Message) => {
    let content = message.content.replace(/^"([\s\S]*)"$/, "$1");

    if (hasBullet(content)) {
      return formatTextWithBullets(content);
    }
    return content;
  };

  const renderMessageContent = (message: Message) => {
    // Show download button if message has a download link
    if (message.downloadLink) {
      return (
        <div className="space-y-2">
          <p className="break-words leading-relaxed whitespace-pre-line mb-2">
            I've prepared a summary for you. Click below to download:
          </p>
          {sessionId ? (
            <a
              href={message.downloadLink}
              download={message.fileName || "Summary.docx"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-blue-100 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {message.fileName || "Download Summary"}
            </a>
          ) : (
            <p className="text-primary-grey-500 text-sm italic">
              Downloadable link, please connect to a session to download the
              summary
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="break-words leading-relaxed whitespace-pre-line">
        {formatMessage(message)}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden relative" style={{ backgroundColor: '#151925' }}>
      {/* chat header  */}
      <div className="min-h-20 flex items-center gap-2 px-4" style={{ borderBottom: '1px solid #1F2937' }}>
        <div className="flex items-center gap-1">
          <ChatIcon />
          <h3
            style={{
              color: 'rgba(255, 255, 255, 0.50)',
              fontFamily: 'Interstate',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: 'normal',
            }}
          >
            Chat
          </h3>
        </div>
      </div>
      {/* Messages Container */}
      <div
        className={cn(
          "flex-1 overflow-y-auto p-5 flex flex-col gap-3",
          "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300"
        )}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className="max-w-[70%] p-3 px-4 relative animate-message-slide font-medium break-words overflow-wrap-anywhere"
            style={
              message.role === "user"
                ? { alignSelf: 'flex-end', borderRadius: '18px 18px 0 18px', backgroundColor: '#171C27', color: '#FFF', wordWrap: 'break-word', overflowWrap: 'anywhere' }
                : { alignSelf: 'flex-start', borderRadius: '18px 18px 18px 0', backgroundColor: '#F5F5F5', color: '#0D0D0D', wordWrap: 'break-word', overflowWrap: 'anywhere' }
            }
          >
            {renderMessageContent(message)}
            <div className="text-[11px] opacity-70 mt-1">
              {formatTime(message.timestamp)}
            </div>
          </div>
        ))}
        {/* Thinking indicator bubble */}
        {isThinking && (
          <div
            className="max-w-[70%] p-3 px-4 relative animate-message-slide font-medium"
            style={{
              alignSelf: 'flex-start',
              borderRadius: '18px 18px 18px 0',
              backgroundColor: '#F5F5F5',
              color: '#0D0D0D',
            }}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Noting down your answer</span>
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Container */}
      <div className="flex p-5 py-4 items-center" style={{ borderTop: '1px solid #1F2937' }}>
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-3 !h-[48px]"
        style={{ width: '100%',borderRadius: '58px',border: '1px solid rgba(55, 65, 81, 0.50)',background: '#1E2330' }}
      >
        <div className="flex-1 relative">
          <TextareaAutosize
            ref={textareaRef}
            value={inputText}
            maxRows={2}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            disabled={!isConnected || !avatarReady || isPaused || isSpeaking || (isStreaming && !isMuted) || thinkingStatus === "completed" || thinkingStatus === "interrupted" || thinkingStatus === "error"}
            className={cn(
              "w-full px-4 py-2 resize-none text-sm outline-none duration-200 bg-transparent disabled:cursor-not-allowed text-white break-words overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-600",
              isStreaming && !isMuted && "!text-blue-400"
            )}
            style={{
              wordWrap: 'break-word',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              maxHeight: '48px',
              scrollbarWidth: 'thin',
              paddingTop: '8px',
              paddingBottom: '6px'
            }}
          />
          {!inputText && (
            <div className="absolute top-1/2 left-0 pl-4 -translate-y-1/2 flex items-center gap-2 pointer-events-none text-sm text-[#9CA3AF]">
              {thinkingStatus === "completed" || thinkingStatus === "interrupted" || thinkingStatus === "error" || !isConnected || !avatarReady ? (
                <>
                  <DisabledPlaceholderIcon />
                  <TypingText text="No input allowed" />
                </>
              ) : isSpeaking ? (
                <>
                  <DisabledPlaceholderIcon />
                  <span>ALIA is speaking, input is disabled...</span>
                </>
              ) : isMuted && isConnected ? (
                <>
                  <MutedPlaceholderIcon />
                  <span>Write Something? I can't hear you...</span>
                </>
              ) : !isMuted && isConnected ? (
                <>
                  <UnmutedPlaceholderIcon />
                  <span>Say Something...</span>
                </>
              ) : null}
            </div>
          )}
        </div>
        {isConnected && avatarReady && thinkingStatus !== "completed" && thinkingStatus !== "interrupted" && thinkingStatus !== "error" && (
          <Button
            type="submit"
            disabled={!inputText.trim() || isPaused}
            className={cn(
              "!w-[32px] !h-[32px] !min-w-[32px] !p-2 !mr-[8px] !rounded-[44px] !bg-[#2D3748] !text-white !border-none !transition-colors !duration-200 hover:!bg-[#3D4A5C]",
              (!inputText.trim() || isPaused) && "[&_path]:!stroke-primary-white-10"
            )}
          >
            <SendIcon />
          </Button>
        )}
      </form>
      </div>
      {/* <ThinkingProcess
        status={thinkingStatus}
        icon={
          thinkingStatus === "completed" ? <CompletedIcon /> :
          thinkingStatus === "interrupted" ? <InterruptedIcon /> :
          undefined
        }
      />  */}
    </div>
  );
};

export default Chat;
